// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import {
  ActionType,
  IAction,
  IActionChangeDashboardWidget,
  IActionTypeLoginResult,
  IActionTypeSelectTariffCircuits,
  IActionTypeSelectGuiItem,
  IActionTypeSelectIos,
  IActionTypeSetFavorite,
} from "actions";
import { isSet } from "shared/utils/utils";
import { EmobilityViewMode, GraphViewMode, IStateCached, PowerViewMode } from "states/cachedstates";
import { IState } from "states/states";
import { EnergySource, InputType, OutputMode } from "shared/types/parameter";

function makeSiteSelection(state: IStateCached) {
  // site selected?
  console.assert(isSet(state.selectedSite), "No site selected");
  const siteId = state.selectedSite ?? 0;
  if (!isSet(state.siteSelection)) {
    state.siteSelection = [];
  }
  if (!isSet(state.siteSelection[siteId])) {
    state.siteSelection[siteId] = {
      dashboardPanel: [],
    };
  }
  return state.siteSelection[siteId];
}

export default function cacheReducer(
  state: IStateCached,
  action: IAction<unknown>,
  rootState: IState,
): IStateCached {
  switch (action.type) {
    case ActionType.ACT_SELECT_SITE:
      // if no payload is given, we keep the cached site
      if (isSet(action.payload)) {
        state.selectedSite = action.payload as number;
      } else {
        console.assert(isSet(state.selectedSite), "No site selected");
      }
      return state;
    case ActionType.ACT_DISCONNECT_SITE:
      state.selectedSite = undefined;
      return state;

    case ActionType.ACT_LOGIN_SUCC: {
      const payload = action.payload as IActionTypeLoginResult;
      // just to be on safe side, remove selected site and chargers for rfid user
      if (payload.isRfidUser) {
        state.selectedSite = undefined;
      }
      return state;
    }

    case ActionType.ACT_SET_FAVORITE: {
      const { doSet, siteId } = action.payload as IActionTypeSetFavorite;
      const favoriteSiteId = siteId ?? state.selectedSite;
      if (!state.favoriteSites) {
        state.favoriteSites = [];
      }
      if (doSet && isSet(favoriteSiteId) && !state.favoriteSites.includes(favoriteSiteId)) {
        state.favoriteSites.push(favoriteSiteId);
      }
      if (!doSet && isSet(favoriteSiteId) && state.favoriteSites.includes(favoriteSiteId)) {
        state.favoriteSites = state.favoriteSites.filter((curSite) => curSite !== favoriteSiteId);
      }
      return state;
    }

    case ActionType.ACT_SELECT_TARIFFCIRCUITS: {
      const data = action.payload as IActionTypeSelectTariffCircuits;
      const selection = makeSiteSelection(state);
      selection.tariffCircuitIds = data.selectedIds!;
      selection.isHierarchyViewMode = data.isHierarchyViewMode ?? selection.isHierarchyViewMode;
      return state;
    }

    case ActionType.ACT_SELECT_IOS: {
      const payload = action.payload as IActionTypeSelectIos;
      const siteSelection = makeSiteSelection(state);
      // determine current selection of inputs
      const selectedInputs =
        siteSelection.inputs ??
        rootState.para.parameter?.input.map((curItem, curIndex) => curItem.id ?? curIndex) ??
        [];
      // split selection in analog and digital inputs
      const nonAnalogInputs = selectedInputs.filter(
        (curItem) =>
          rootState.para.parameter?.input.find(
            (curInput, curIndex) => (curInput.id ?? curIndex) === curItem,
          )?.type !== InputType.ANALOG,
      );
      const nonDigitalInputs = selectedInputs.filter((curItem) => {
        const inputType = rootState.para.parameter?.input.find(
          (curInput, curIndex) => (curInput.id ?? curIndex) === curItem,
        )?.type;
        return inputType === undefined || inputType === InputType.ANALOG;
      });
      // put together new selection with remaining type and new selection
      siteSelection.inputs =
        payload.type === "analog" ?
          [...nonAnalogInputs, ...payload.inputIds]
        : [...nonDigitalInputs, ...payload.inputIds];

      // do the same for outputs
      const selectedOutputs =
        siteSelection.outputs ??
        rootState.para.parameter?.output.map((curItem, curIndex) => curItem.id ?? curIndex) ??
        [];
      const nonAnalogOutputs = selectedOutputs.filter(
        (curItem) =>
          rootState.para.parameter?.output.find(
            (curOutput, curIndex) => (curOutput.id ?? curIndex) === curItem,
          )?.mode !== OutputMode.ANALOG,
      );
      const nonDigitalOutputs = selectedOutputs.filter((curItem) => {
        const outputMode = rootState.para.parameter?.output.find(
          (curOutput, curIndex) => (curOutput.id ?? curIndex) === curItem,
        )?.mode;
        return outputMode === undefined || outputMode === OutputMode.ANALOG;
      });
      siteSelection.outputs =
        payload.type === "analog" ?
          [...nonAnalogOutputs, ...payload.outputIds]
        : [...nonDigitalOutputs, ...payload.outputIds];

      return state;
    }

    case ActionType.ACT_SELECT_MONITOR: {
      const data = action.payload as IActionTypeSelectTariffCircuits;
      console.assert(data.selectedIds?.length === 1, "Only one control circuit can be selected");
      makeSiteSelection(state).controlCircuitId = data.selectedIds?.[0];
      return state;
    }

    case ActionType.ACT_SELECT_TRENDMONITOR:
      makeSiteSelection(state).trendMonitor = action.payload as number;
      return state;

    case ActionType.ACT_SELECT_EMOBILITY_WIDGET: {
      const data = action.payload as IActionTypeSelectGuiItem;
      makeSiteSelection(state).eMobilityViewMode = data.selection as EmobilityViewMode;
      return state;
    }

    case ActionType.ACT_SELECT_ENERGYSOURCE: {
      const data = action.payload as IActionTypeSelectGuiItem;
      const siteSelection = makeSiteSelection(state);
      // check for change
      if (siteSelection.energySource !== data.selection) {
        siteSelection.energySource = data.selection as EnergySource | undefined;
        // also reset hierarchy and circuits as they may not be valid for the new energy source
        siteSelection.hierarchyId = undefined;
        siteSelection.category = undefined;
        siteSelection.tariffCircuitIds = undefined;
      }
      return state;
    }

    case ActionType.ACT_SELECT_CATEGORY: {
      const data = action.payload as IActionTypeSelectGuiItem;
      const siteSelection = makeSiteSelection(state);
      // check for change
      if (siteSelection.category !== data.selection) {
        siteSelection.category = data.selection;
      }
      return state;
    }

    case ActionType.ACT_SELECT_GRAPHVIEWMODE: {
      const data = action.payload as IActionTypeSelectGuiItem;
      makeSiteSelection(state).graphViewMode = data.selection as GraphViewMode;
      return state;
    }

    case ActionType.ACT_SELECT_POWERVIEWMODE: {
      const data = action.payload as IActionTypeSelectGuiItem;
      makeSiteSelection(state).powerViewMode = data.selection as PowerViewMode;
      return state;
    }

    case ActionType.ACT_SELECT_DASHBOARD_WIDGET: {
      const payload = action.payload as IActionChangeDashboardWidget;
      const siteSelection = makeSiteSelection(state);
      siteSelection.dashboardPanel[payload.index] = payload.panel;
      return state;
    }

    default:
      return state;
  }
}
